/**
 * Maps SDS customer batch payloads (Create Task + Get Batch Task) onto the
 * portal ProgressCard shape. Create returns snake_case only (`batch_name`,
 * `batch_state`) and does not include list-only `batchState`.
 */

function firstNonEmpty(...values: unknown[]): string {
  for (const value of values) {
    if (value === undefined || value === null) continue;
    const text = String(value).trim();
    if (text && text !== "<nil>") return text;
  }
  return "";
}

function toNumber(value: unknown, fallback = 0): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

/** Canonical key from API `batch_state` (preferred) or legacy `batchState` / `status`. */
export function customerBatchStateKey(row: Record<string, unknown> | null | undefined): string {
  if (!row || typeof row !== "object") return "";
  const raw = firstNonEmpty(row.batch_state, row.batchState, row.status);
  return raw.toUpperCase().replace(/\s+/g, "_");
}

export function isCustomerBatchFinished(
  row: Record<string, unknown> | null | undefined
): boolean {
  const key = customerBatchStateKey(row);
  return key === "FINISHED" || key === "COMPLETED";
}

/** Portal display state expected by ProgressCard + status filters. */
export function customerBatchDisplayState(row: Record<string, unknown>): string {
  const key = customerBatchStateKey(row);
  switch (key) {
    case "CREATED":
    case "INITIALIZED":
    case "WAITING_FOR_APPROVAL":
      return "Waiting for approval";
    case "REGISTERED":
      return "Preparing";
    case "RUNNING":
    case "PROGRESS":
      return "PROGRESS";
    case "PAUSED":
      return "PAUSED";
    case "FINISHED":
    case "COMPLETED":
      return "FINISHED";
    case "ERROR":
      return "ERROR";
    default:
      return key;
  }
}

export function matchesCustomerBatchFilter(
  row: Record<string, unknown> | null | undefined,
  filterValue: string
): boolean {
  if (!filterValue || !row) return true;
  return customerBatchDisplayState(row) === filterValue;
}

export function unwrapCustomerBatchList(payload: unknown): unknown[] {
  if (Array.isArray(payload)) return payload;
  if (payload && typeof payload === "object") {
    const nested = (payload as { data?: unknown }).data;
    if (Array.isArray(nested)) return nested;
  }
  return [];
}

export function normalizeCustomerBatchTask(row: any): any {
  if (!row || typeof row !== "object") return row;

  const batchState = customerBatchDisplayState(row);
  const downloadUrl = firstNonEmpty(row.download_url, row.downloadUrl);
  const productArn = firstNonEmpty(row.product_arn, row.batchType, row.batch_type).toLowerCase();

  return {
    ...row,
    batchId: firstNonEmpty(row.batch_id, row.batchId, row.id),
    batchName: firstNonEmpty(row.batch_name, row.name, row.batchName),
    batch_state: firstNonEmpty(row.batch_state, row.batchState, row.status),
    batchState,
    status: batchState,
    batchType: productArn,
    orgId: firstNonEmpty(row.client_org_id, row.org_id, row.orgId),
    orgName: firstNonEmpty(row.org_name, row.orgName),
    uploadSource: firstNonEmpty(row.upload_source, row.uploadSource) || null,
    batchMode: firstNonEmpty(row.batch_mode, row.batchMode),
    isBillable: firstNonEmpty(row.is_billable, row.isBillable),
    tpm: row.tpm,
    totalItems: toNumber(row.total_items ?? row.totalItems),
    totalProcessed: toNumber(
      row.total_processed ?? row.totalProcessed ?? row.total_completed
    ),
    created_at: row.created_at,
    updated_at: row.updated_at ?? row.created_at,
    downloadUrl,
    downloadAvailable: Boolean(
      row.download_available ??
        row.downloadAvailable ??
        downloadUrl
    ),
    downloadInitiated: Boolean(
      row.download_initiated ?? row.downloadInitiated ?? false
    ),
  };
}

export function normalizeCustomerBatchTasks(rows: unknown): any[] {
  return unwrapCustomerBatchList(rows).map(normalizeCustomerBatchTask);
}

export function parseCustomerCreateTaskResponse(payload: any): {
  success: boolean;
  message: string;
  data: any | null;
} {
  if (!payload || typeof payload !== "object") {
    return { success: false, message: "Something went wrong", data: null };
  }
  const inner =
    payload.data && typeof payload.data === "object" && !Array.isArray(payload.data)
      ? payload.data
      : payload;
  const errorMessage = firstNonEmpty(
    payload.error?.message,
    payload.message
  );
  if (payload.success === true) {
    return {
      success: true,
      message: firstNonEmpty(payload.message, "Successfully Created Batch"),
      data: normalizeCustomerBatchTask(inner),
    };
  }
  return {
    success: false,
    message: errorMessage || "Something went wrong",
    data: null,
  };
}
